import React from "react";
import { HiEllipsisHorizontal } from "react-icons/hi2";
import config from "../../config";
import FastAPIClient from "../../client";
import { useLocation, useNavigate } from "react-router-dom";
const client = new FastAPIClient(config);
import Notification from "./Notification";
import NotificationSkeleton from "../Skeletons/NotificationSkeleton";
import { IntlProvider, FormattedMessage } from "react-intl";
import messages_en from "./translations/en.json";
import messages_fr from "./translations/fr.json";
import { NotificationType } from "../../types/notifications.t";
import { UserType } from "../../types/user";
import { Button, Menu, MenuItem } from "@mui/material";
import { useNetwork } from "../NetworkProvider";
import useMediaQuery from "@mui/material/useMediaQuery";

interface NotificationsSideBarProps {
    user_id: string;
    onClose: () => void;
    locale: string;
    notifications?: NotificationType[]; // Make optional since we get from context
    notificationUsers?: UserType[]; // Make optional for now
}

const messages = {
    en: messages_en,
    fr: messages_fr,
};

const NotificationsSideBar: React.FC<NotificationsSideBarProps> = ({
    user_id,
    onClose,
    locale,
    notifications: propNotifications,
    notificationUsers: propNotificationUsers,
}) => {
    // Get notifications from NetworkProvider context
    const {
        notifications: contextNotifications,
        setNotifications,
        notificationUsers: contextNotificationUsers,
        notificationsLoading,
        isOnline,
    } = useNetwork();
    const isMobile = useMediaQuery("(max-width: 600px)");

    // Use prop notifications if provided, otherwise use context notifications
    const notifications = propNotifications || contextNotifications || [];
    const notificationUsers =
        propNotificationUsers || contextNotificationUsers || [];

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl && isOnline);
    const location = useLocation();
    const navigate = useNavigate();

    const handleClickNotification = (notification: NotificationType) => {
        if (!isOnline) {
            return;
        }
        if (isMobile) {
            // On mobile, add notifications=true to URL before navigating
            const targetParams = new URLSearchParams();
            targetParams.set("notifications", "true");

            // First, add a history entry for where we want to return to
            navigate({
                pathname: location.pathname,
                search: targetParams.toString(),
            });
        }

        // Navigate to the notification link (same for both desktop and mobile)
        navigate(notification.link);
    };
    const todayNotifications = notifications.filter((notification) => {
        const notificationDate = new Date(notification.created_at);
        const today = new Date();
        return notificationDate.toDateString() === today.toDateString();
    });
    const olderNotifications = notifications.filter((notification) => {
        const notificationDate = new Date(notification.created_at);
        const today = new Date();
        return notificationDate.toDateString() !== today.toDateString();
    });

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event?: React.MouseEvent) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setAnchorEl(null);
    };
    const handleMarkAllAsRead = async (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        await client.markAllNotificationsAsRead(user_id);
        setNotifications(
            notifications.map((notification) => ({
                ...notification,
                is_read: true,
            }))
        );
        handleMenuClose();
    };

    return (
        <IntlProvider locale={locale} messages={messages[locale]}>
            <div className="flex-1 flex flex-col overflow-y-auto pb-20 md:pb-0 w-full h-full">
                <div className="flex justify-between items-center px-3 pt-4 pb-3 w-full">
                    <h2 className="text-lg font-semibold">
                        <FormattedMessage
                            id="notifications.title"
                            defaultMessage="Notifications"
                        />
                    </h2>
                    {isOnline && (
                        <div>
                            <Button
                                color="secondary"
                                sx={{ minWidth: "25px", padding: "4px" }}
                                onClick={handleMenuClick}
                            >
                                <HiEllipsisHorizontal size={20} />
                            </Button>
                            {anchorEl && (
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleMenuClose}
                                    onClick={(e) => e.stopPropagation()}
                                    BackdropProps={{
                                        onClick: (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleMenuClose();
                                        },
                                    }}
                                    MenuListProps={{
                                        onClick: (e) => e.stopPropagation(),
                                    }}
                                >
                                    <MenuItem onClick={handleMarkAllAsRead}>
                                        <FormattedMessage
                                            id="notifications.markAllAsRead"
                                            defaultMessage="Mark all as read"
                                        />
                                    </MenuItem>
                                </Menu>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto pl-3 w-full">
                    {notificationsLoading && (
                        <div className="flex flex-col gap-4">
                            {Array.from({ length: 8 }, (_, index) => (
                                <NotificationSkeleton key={index} />
                            ))}
                        </div>
                    )}
                    {/* Notifications List */}
                    {notifications.length === 0 && !notificationsLoading && (
                        <div className="p-4 text-center text-gray-500">
                            <FormattedMessage
                                id="notifications.noNotifications"
                                defaultMessage="No notifications"
                            />
                        </div>
                    )}
                    {((!notificationsLoading &&
                        todayNotifications.length > 0 &&
                        olderNotifications.length === 0) ||
                        (!notificationsLoading &&
                            todayNotifications.length === 0 &&
                            olderNotifications.length > 0)) && (
                        <div className="divide-y w-full">
                            {notifications.map((notification) => (
                                <Notification
                                    key={notification.notification_id}
                                    notification={notification}
                                    notifications={notifications}
                                    setNotifications={setNotifications}
                                    onClose={onClose}
                                    onClick={handleClickNotification}
                                    notificationUsers={notificationUsers}
                                />
                            ))}
                        </div>
                    )}
                    {!notificationsLoading &&
                        todayNotifications.length > 0 &&
                        olderNotifications.length > 0 && (
                            <div className="w-full">
                                <h3
                                    className="flex gap-3 w-full  font-semibold text-gray-700 text-left"
                                    style={{
                                        fontSize: "1.1rem",
                                    }}
                                >
                                    <div className="w-4 md:hidden" />

                                    <FormattedMessage
                                        id="notifications.today"
                                        defaultMessage="Today"
                                    />
                                </h3>

                                <div className="divide-y">
                                    {todayNotifications.map((notification) => (
                                        <Notification
                                            key={notification.notification_id}
                                            notification={notification}
                                            notifications={notifications}
                                            setNotifications={setNotifications}
                                            onClose={onClose}
                                            onClick={handleClickNotification}
                                            notificationUsers={
                                                notificationUsers
                                            }
                                        />
                                    ))}
                                </div>
                                <h3
                                    className="flex gap-3 w-full  font-semibold text-gray-700 text-left"
                                    style={{
                                        fontSize: "1.1rem",
                                    }}
                                >
                                    <div className="w-4 md:hidden" />
                                    <FormattedMessage
                                        id="notifications.older"
                                        defaultMessage="Older"
                                    />
                                </h3>
                                <div className="divide-y">
                                    {olderNotifications.map((notification) => (
                                        <Notification
                                            key={notification.notification_id}
                                            notification={notification}
                                            notifications={notifications}
                                            setNotifications={setNotifications}
                                            onClose={onClose}
                                            onClick={handleClickNotification}
                                            notificationUsers={
                                                notificationUsers
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                </div>
                <div className="h-10 md:hidden" />
            </div>
        </IntlProvider>
    );
};

export default NotificationsSideBar;
